import { useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Detects navigation direction (forward / back) for use with
 * AnimatePresence `custom` props.
 *
 * Direction is determined by comparing `window.history.state.idx` across
 * renders (works for PUSH / POP navigations). For replace-based navigations
 * — where the history index stays the same — callers can pass
 * `location.state.back` (boolean) to signal direction explicitly.
 *
 * The result is latched in a ref so that re-renders during an exit
 * animation (e.g. from Redux store updates) don't flip the direction.
 */
export const useNavigationDirection = (): boolean => {
  const location = useLocation();

  const stateBack = (location.state as { back?: boolean } | null)?.back;
  const historyIdx =
    (window.history.state as { idx?: number } | null)?.idx ?? 0;

  const prevIdxRef = useRef(historyIdx);
  const isBackRef = useRef(false);
  const prevKeyRef = useRef(location.key);

  if (historyIdx !== prevIdxRef.current) {
    isBackRef.current = historyIdx < prevIdxRef.current;
    prevIdxRef.current = historyIdx;
  }

  if (location.key !== prevKeyRef.current) {
    if (stateBack === true) {
      isBackRef.current = true;
    } else if (stateBack === false) {
      isBackRef.current = false;
    }
    prevKeyRef.current = location.key;
  }

  // Explicit state.back (from replace-based navigations where historyIdx
  // doesn't change) takes priority over the latched ref.
  return typeof stateBack === 'boolean' ? stateBack : isBackRef.current;
};
